import React, { useEffect, useState } from 'react'
import { Check, Eye, EyeOff, Lock, X } from 'lucide-react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem, FormMessage, FormLabel } from '@/components/ui/form'
import { resetPasswordSchema, ResetPasswordSchema } from '@/entities/user'
import { showConfirm } from '@/lib/alerts'
import { api } from '@/lib/axios'
import { useToast } from '@/hooks/use-toast'
import { HTTP_STATUS_CODE } from '@/constants/http'
import { useLoader } from '@/contexts/LoaderContext'
import { Input } from '@/components/ui_custom/input'
import { useLocation } from 'react-router-dom'
import { Logo } from '@/components/common/logo'

// variables
const loc = 'pages/ResetPassword'

export function ResetPassword() {
  const { toast } = useToast()
  const location = useLocation()
  const { showLoader, hideLoader, isLoading } = useLoader()
  const [isShowingPassword, setIsShowingPassword] = useState(false)
  const [isShowingConfirmPassword, setIsShowingConfirmPassword] = useState(false)
  const [isInvalidCode, setIsInvalidCode] = useState(false)
  const code = location.pathname.split('/reset-password/')[1] || '/'

  const form = useForm<ResetPasswordSchema>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      code: code,
      password: '',
      confirmPassword: ''
    }
  })

  const formValues = useWatch({ control: form.control })

  const _handleChangePassword = async () => {
    try {
      const isValid = await form.trigger()
      if (!isValid) return

      const { isConfirmed } = await showConfirm('Deseja confirmar?')
      if (!isConfirmed) return

      showLoader()

      const response = await api.post('/', {
        ...formValues,
        action: 'reset-password'
      })

      if ([HTTP_STATUS_CODE.OK].includes(response.data?.statusCode)) {
        toast({
          variant: 'success',
          title: 'Sucesso',
          description: response.data?.statusMessage || `Senha alterada com sucesso.`
        })
        setTimeout(() => {
          window.location.href = '/login'
        }, 3000)
      } else toast({ variant: 'destructive', title: 'Ops ...', description: response.data?.statusMessage || 'Não foi possível alterar a senha.' })
    } catch (err) {
      toast({ variant: 'destructive', title: 'Ops ...', description: 'Não foi possível alterar a senha.' })
      console.error(`Unhandled error at ${loc}._handleChangePassword. Details: ${err}`)
    } finally {
      hideLoader()
    }
  }

  useEffect(() => {
    if (code == '/') {
      toast({ variant: 'destructive', title: 'Ops ...', description: 'Código inválido. Por favor, verifique o link enviado para seu e-mail.' })
      setIsInvalidCode(true)
    }
  }, [code])

  return (
    <div className="app-shell min-h-screen min-w-full flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8 bg-card p-8 rounded-3xl shadow-elevated border border-border/50">
        <div className="flex justify-center items-center cursor-pointer" onClick={() => (window.location.href = '/')}>
          <Logo size="md" invert={true} />
        </div>

        <h2 className="mt-6 text-center text-2xl font-bold text-foreground">Redefinir sua senha</h2>

        <Form {...form}>
          <form
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault()
                _handleChangePassword()
              }
            }}
          >
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="w-full mb-5">
                  <FormLabel>Nova senha *</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Digite a nova senha"
                      disabled={isLoading || isInvalidCode}
                      type={isShowingPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      required
                      leftIcon={<Lock className="h-5 w-5" />}
                      rightIcon={isShowingPassword ? <EyeOff className="w-5 h-5 hover:cursor-pointer z-[100]" onClick={() => setIsShowingPassword(prev => !prev)} /> : <Eye className="w-5 h-5 hover:cursor-pointer z-[100]" onClick={() => setIsShowingPassword(prev => !prev)} />}
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem className="w-auto mb-5">
                  <FormLabel>Confirmar nova senha *</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Digite a confirmação da nova senha"
                      disabled={isLoading || isInvalidCode}
                      type={isShowingConfirmPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      required
                      leftIcon={<Lock className="h-5 w-5" />}
                      rightIcon={isShowingConfirmPassword ? <EyeOff className="w-5 h-5 hover:cursor-pointer z-[100]" onClick={() => setIsShowingConfirmPassword(prev => !prev)} /> : <Eye className="w-5 h-5 hover:cursor-pointer z-[100]" onClick={() => setIsShowingConfirmPassword(prev => !prev)} />}
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="col-span-2 mt-4 pt-4 border-t flex items-center gap-x-2 justify-end">
              <Button variant="default" type="button" className="flex gap-x-2" onClick={_handleChangePassword} disabled={isInvalidCode} loading={isLoading}>
                Confirmar
                <Check className="size-5" />
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  )
}
